import React, { useState, useEffect, useCallback } from "react";
import { IEvent } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";
import { extractJudgeScheduleFromRings } from "@eagerdog/judge-schedule-util";
import { useSearchParams } from "react-router-dom";

import { apiService } from "src/services/api.service";

import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import { toast } from "src/components/Toast/ToastManager";


import type { ColDef } from "ag-grid-community";
import { GridApi, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";
import { StatusRenderer } from "./StatusRenderer";

import styles from "./WaitlistTab.module.scss";
import { download } from "../../../components/Download/DownloadManager";

interface IProps {
  event: IEvent,
  currentTab: number,
  tabIndex: number
}

const HERDING_SHOW_TYPES = [
  Constants.show_type.herding,
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
];

interface IRow {
  position: number | null,
  callName: string,
  armNumber: string,
  status: string,
  breed: string,
  ownerName: string,
  ownerEmail: string,
  show_id: string,
  showName: string,
  showDate: string,
  showElement: string,
  showLevel: string,
  showSection: string,
  registeredAt: string,
  approvedWithoutPaymentAt: string,
  isLottery: boolean,
  lotteryRanAt?: string,
  actions: any
}

const WaitlistTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [cancelingWaitlists, setCancelingWaitlists] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  
  // Helper function to format minutes into "Xh Ym (Zm)" format
  const formatMinutesDisplay = (minutes: number): string => {
    if (!minutes || minutes === 0) {
      return "0h 0m (0.0m)";
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.floor(minutes % 60);
    
    return `${hours}h ${remainingMinutes}m (${minutes.toFixed(1)}m)`;
  };
  const [gridApi, setGridApi] = useState<GridApi<IRow>>();
  const [show, setShow] = useState<IOption>({ value: "All Shows", id: "None" });
  const [showOptions, setShowOptions] = useState<IOption[]>([]);
  const [judgeSchedules, setJudgeSchedules] = useState<any[]>([]);
  const [allScores, setAllScores] = useState<any[]>([]);
  const [allShows, setAllShows] = useState<any[]>([]);
  const [judgesExpanded, setJudgesExpanded] = useState<boolean>(true);


  const { isShown, toggle } = useModal();

  const menuActions:IContextMenuOption[] = [
    {
      label: "Cancel All",
      onClick: () => {
        toggle();
      }
    },
    {
      label: "Export Waitlists",
      onClick: () => {
        download.pushDownload({
          type: "waitlists",
          eventId: props.event._id,
          showId: show.id === "None" ? undefined : show.id
        });
      }
    }
  ];

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const getShows = useCallback((clubId: string, eventId: string) => {
    apiService.getClubEventShows(clubId, eventId).then((response) => {
      let options:IOption[] = [{ value: "All Shows", id: "None" }];

      for (let i in response) {
        let show:any = response[i];

        options.push({
          value: show.show_type + " " + show.show_name + " (" + moment(show.show_date).format("ddd, h:mm a") + ")",
          id: show._id,
          data: show
        });
      }

      setShowOptions(options);
      setAllShows(response);
    }).catch((error) => {
      console.error('Error fetching shows:', error);
    });
    
    // Fetch scores for judge schedule calculations
    apiService.getEventScores(eventId).then((scores) => {
      setAllScores(scores);
    }).catch((error) => {
      console.error('Error fetching scores:', error);
    });
  }, []);

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setShow({ id: "None", value: "All Shows" });
      gridApi.setFilterModel({
        "status": {
          "values": ["waitlist", "awaiting_payment", "canceled", "registered", "refunded", "expired", "declined"],
          "filterType": "set"
        }
      });
    }
  }, [gridApi]);



  const getWaitlists = useCallback((id: string) => {
    let query:any = {
      sort: [{
        attribute_name: "created_at",
        sort: "asc"
      }]
    };

    apiService.getEventWaitlists(id, query).then((response) => {
      let rows:IRow[] = [];

      for (let i in response) {
        let row:any = response[i];

        // Don't show position for registered entries
        const isRegistered = row.status === "registered";

        rows.push({
          position: isRegistered ? null : row.dog_class.waitlist_position,
          callName: row.call_name,
          armNumber: row.arm_number || "",
          status: row.status,
          breed: row.breed,
          ownerName: row.owner_name || "",
          ownerEmail: row.owner_email || "",
          show_id: row.show_id,
          showName: row.show_type + " " + row.show_name,
          showDate: moment(row.show_date).format("MM/DD/YYYY"),
          showElement: row.dog_class.show_element,
          showLevel: row.dog_class.level || "",
          showSection: row.dog_class.section || "",
          registeredAt: moment(row.created_at).format("MMM Do YYYY, h:mmA zz"),
          approvedWithoutPaymentAt: row.approved_without_payment_at
            ? moment(row.approved_without_payment_at).format("MMM Do YYYY, h:mmA zz")
            : "",
          isLottery: !!props.event?.is_lottery,
          lotteryRanAt: props.event?.lottery_ran_at
            ? String(props.event.lottery_ran_at)
            : undefined,
          actions: {
            waitlist: row
          }
        });
      }

      setRowData(rows);
    });
  }, [props.event?.is_lottery, props.event?.lottery_ran_at]);

  let colDefs:ColDef<IRow>[] = [
    { 
      field: "position", 
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellDataType: "number", 
      filter: true,
      sort: "asc"
    },
    { field: "callName", minWidth: 150, cellDataType: "text", filter: true },
    {
      field: "armNumber",
      headerName: "Arm #",
      minWidth: 120,
      cellDataType: "text",
      filter: true,
      // Only relevant once a specific AKC Herding trial is selected - waitlist
      // "Alternate" armbands (e.g. "A103-ALT") are assigned per-trial, so they
      // aren't meaningful across "All Shows".
      hide: !(show.id !== "None" && HERDING_SHOW_TYPES.includes(show.data?.show_type)),
    },
    {
      field: "status",
      minWidth: 160,
      filter: true,
      cellRenderer: StatusRenderer,
    },
    { field: "breed", minWidth: 190, cellDataType: "text", filter: true },
    { field: "ownerName", headerName: "Owner Name", minWidth: 170, cellDataType: "text", filter: true },
    { field: "ownerEmail", headerName: "Owner Email", minWidth: 200, cellDataType: "text", filter: true },
    { field: "show_id", filter: true, hide: true },
    { field: "showName", minWidth: 150, cellDataType: "text", filter: true },
    { field: "showDate", minWidth: 150, cellDataType: "text", filter: true },
    { field: "showElement", minWidth: 170, cellDataType: "text", filter: true },
    { field: "showLevel", minWidth: 150, cellDataType: "text", filter: true },
    {
      field: "showSection",
      headerName: "Stock",
      minWidth: 120,
      cellDataType: "text",
      filter: true,
      hide: !allShows.some((s: any) => HERDING_SHOW_TYPES.includes(s.show_type)),
    },
    { field: "registeredAt", minWidth: 180, cellDataType: "text", filter: true },
    {
      field: "approvedWithoutPaymentAt",
      headerName: "Approved W/O Payment",
      minWidth: 210,
      filter: true,
      cellStyle: (params: any) => params.value ? { color: "#c0392b", fontWeight: 600 } : null,
    },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        getWaitlists: () => { getWaitlists(props.event._id); },
        event: props.event
      },
      resizable: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const onFirstDataRendered = (params:any) => {
    const statuses = ["waitlist", "awaiting_payment", "canceled", "registered", "refunded", "expired", "declined"];
    if (props.event.is_lottery) {
      statuses.push("awaiting_payment");
    }
    params.api.setFilterModel({
        "status": {
            "values": statuses,
            "filterType": "set"
        }
    });
  }

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  }

  const [rowData, setRowData] = useState<IRow[]>([]);

  const defaultColDef: ColDef = {
    flex: 1
  };

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getWaitlists(props.event._id);
      if (props.event.club) {
        getShows(props.event.club._id, props.event._id);
      }
      // Refresh scores when tab becomes active to get latest entry counts
      apiService.getEventScores(props.event._id).then((scores) => {
        setAllScores(scores);
      }).catch((error) => {
        console.error('Error fetching scores:', error);
      });
    }
  }, [props.currentTab, props.tabIndex, props.event._id, props.event.club, getWaitlists, getShows]);

  useEffect(() => {
    if (!loaded) {
      getWaitlists(props.event._id);
      if (props.event.club) {
        getShows(props.event.club._id, props.event._id);
      }
      setLoaded(true);
    }
  }, [loaded, getWaitlists, getShows, props.event._id, props.event.club]);

  useEffect(() => {
    const waitlistShowId = searchParams.get("waitlist_show");
    if (!waitlistShowId) return;
    if (props.currentTab !== props.tabIndex) return;
    if (!showOptions.length) return;
    if (show.id !== "None") return;

    const selectedShowOption = showOptions.find((option) => option.id === waitlistShowId);
    if (selectedShowOption) {
      setShow(selectedShowOption);
    }
  }, [searchParams, showOptions, show.id, props.currentTab, props.tabIndex]);

  useEffect(() => {
    let filter: any = {
      "status": {
        "values": ["waitlist", "awaiting_payment", "canceled", "registered", "refunded", "expired", "declined"],
        "filterType": "set"
      }
    };

    if (gridApi && show.id !== "None") {
      filter["show_id"] = {
        "values": [ show.id ],
        "filterType": "set",
      };
      
      // Calculate judge schedules for the selected show
      const selectedShow = show.data;
      if (selectedShow && allScores.length > 0 && allShows.length > 0) {
        // Get judge schedules for this show using extractJudgeScheduleFromRings
        const showJudgeSchedules = extractJudgeScheduleFromRings(selectedShow, undefined, allScores, props.event);
        
        // Calculate daily totals for each judge
        const currentDate = moment(selectedShow.show_date).format('YYYY-MM-DD');
        const showsOnDate = allShows.filter(s => 
          moment(s.show_date).format('YYYY-MM-DD') === currentDate
        );
        
        const judgeSchedulesWithDaily = showJudgeSchedules.map(judgeSchedule => {
          let dailyTotal = 0;
          
          // Sum up this judge's judging time across all shows on the same date.
          // judgingMinutes excludes breaks, matching the Manage Show judge schedule
          // and the capacity calculations.
          showsOnDate.forEach(s => {
            const schedules = extractJudgeScheduleFromRings(s, undefined, allScores, props.event);
            const matchingJudge = schedules.find(js => js.judgeName === judgeSchedule.judgeName);
            if (matchingJudge) {
              dailyTotal += matchingJudge.judgingMinutes;
            }
          });
          
          return {
            ...judgeSchedule,
            dailyTotalMinutes: dailyTotal
          };
        });
        
        setJudgeSchedules(judgeSchedulesWithDaily);
      } else {
        setJudgeSchedules([]);
      }
    } else {
      setJudgeSchedules([]);
    }

    if (gridApi) {
      gridApi.setFilterModel(filter);
    }
  }, [gridApi, show, allScores, allShows, props.event]);



  return (
    <div className={styles.WaitlistTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          <Dropdown 
            className={styles.dropdown} 
            value={show} 
            onChange={(e: any, value: IOption) => { setShow(value); }} 
            label="Show" 
            options={showOptions} 
            placeholder="Select a Show" 
          />
          <Button className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
        </div>
        <div className={styles.right}>
          <ContextMenu
            label={"Actions"}
            options={menuActions}
          />
        </div>
      </div> 

      {judgeSchedules.length > 0 && show.id !== "None" && (
        <div className={styles.judgeSchedules}>
          <div className={styles.judgeHeader}>
            <button 
              className={styles.toggleButton}
              onClick={() => setJudgesExpanded(!judgesExpanded)}
            >
              {judgesExpanded ? 'Hide Judges' : 'Show Judges'}
            </button>
          </div>
          {judgesExpanded && (
            <div className={styles.judgeList}>
              {judgeSchedules.map((judgeSchedule, index) => {
                const trialMinutes = judgeSchedule.judgingMinutes;
                const dailyMinutes = judgeSchedule.dailyTotalMinutes;
                
                return (
                  <div key={index} className={styles.judgeCard}>
                    <div className={styles.judgeName}>
                      {judgeSchedule.judgeName} ({judgeSchedule.totalEntries} {judgeSchedule.totalEntries === 1 ? 'Entry' : 'Entries'})
                    </div>
                    <div className={styles.judgeTime}>
                      <span className={styles.daily}>
                        <strong>{moment(show.data.show_date).format("MMMM Do")} - Daily Total - </strong>{formatMinutesDisplay(dailyMinutes)}
                      </span>
                      <span className={styles.trial}>
                        <strong>{moment(show.data.show_date).format("MMMM Do")} - {show.data.custom_show_name || show.data.show_name} - </strong>{formatMinutesDisplay(trialMinutes)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      
      <div style={{ minHeight: "520px", display: "flex", flexDirection: "column" }}>
        <AgGridReact
          rowData={rowData}
          suppressRowTransform={true}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          onFirstDataRendered={onFirstDataRendered}
          onGridReady={onGridReady}
          theme={theme}
          pagination={true}
          paginationPageSize={20}
        />
      </div>

    <Modal
      className="confirmModal"
      isShown={isShown}
      hide={toggle}
      modalContent={<Confirm
        message={<><p>Are you sure you want to cancel all outstanding waitlist registrations?</p></>}
        buttonsDisabled={cancelingWaitlists}
        onConfirm={() => {
          setCancelingWaitlists(true);

          apiService.cancelEventWaitlistEntries(props.event._id).then((response) => {
            getWaitlists(props.event._id);

            toast.show({
              title: "Waitlists",
              content: "You've successfully canceled all waitlists",
              duration: 10000,
              type: "success"
            });
          }).catch((error:any) => {
            toast.show({
              title: "Waitlists",
              content: error.response.data.message ? error.response.data.message : "Something went wrong, please try again later",
              duration: 10000,
              type: "fail"
            });
          }).finally(() => {
            setCancelingWaitlists(false);
            toggle();
          });
        }}
        onCancel={() => {
          toggle();
        }}
      />}
    />
    </div>
  );
};

export default WaitlistTab;